var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { StreamPartIDUtils, toUserId, toUserIdRaw } from '@streamr/utils';
import { Lifecycle, delay, inject, scoped } from 'tsyringe';
import { v4 as uuidv4 } from 'uuid';
import { Identity, IdentityInjectionToken } from '../identity/Identity';
import { ConfigInjectionToken } from '../ConfigTypes';
import { NetworkNodeFacade } from '../NetworkNodeFacade';
import { StreamRegistry } from '../contracts/StreamRegistry';
import { MessageID } from '../protocol/MessageID';
import { StreamMessageType } from '../protocol/StreamMessage';
import { createRandomMsgChainId } from '../publish/messageChain';
import { MessageSigner } from '../signature/MessageSigner';
import { SignatureValidator } from '../signature/SignatureValidator';
import { LoggerFactory } from '../utils/LoggerFactory';
import { pOnce, withThrottling } from '../utils/promises';
import { MaxSizedSet } from '../utils/utils';
import { validateStreamMessage } from '../utils/validateStreamMessage';
import { LocalGroupKeyStore } from './LocalGroupKeyStore';
import { EncryptionUtil } from './EncryptionUtil';
import { AsymmetricEncryptionType, ContentType, EncryptionType, GroupKeyRequest, GroupKeyResponse, SignatureType } from '@streamr/trackerless-network';
import { createCompliantExchangeKeys } from '../utils/encryptionCompliance';
import { StreamrClientError } from '../StreamrClientError';
import { Tokens } from '../tokens';
const MAX_PENDING_REQUEST_COUNT = 50000; // just some limit, we can tweak the number if needed
/*
 * Sends group key requests and receives group key responses
 */
let SubscriberKeyExchange = class SubscriberKeyExchange {
    keyPair;
    pendingRequests = new MaxSizedSet(MAX_PENDING_REQUEST_COUNT);
    networkNodeFacade;
    streamRegistry;
    signatureValidator;
    messageSigner;
    store;
    subscriber;
    identity;
    logger;
    ensureStarted;
    config;
    requestGroupKey;
    constructor(networkNodeFacade, streamRegistry, signatureValidator, messageSigner, store, subscriber, config, identity, loggerFactory) {
        this.networkNodeFacade = networkNodeFacade;
        this.streamRegistry = streamRegistry;
        this.signatureValidator = signatureValidator;
        this.messageSigner = messageSigner;
        this.store = store;
        this.subscriber = subscriber;
        this.identity = identity;
        this.logger = loggerFactory.createLogger('SubscriberKeyExchange');
        this.config = config;
        // Setting explicit keys disables the key-exchange
        if (config.encryption.keys === undefined) {
            this.ensureStarted = pOnce(async () => {
                this.keyPair = await createCompliantExchangeKeys(identity, config);
                networkNodeFacade.addMessageListener((msg) => this.onMessage(msg));
                this.logger.debug('Started');
            });
            this.requestGroupKey = withThrottling((groupKeyId, publisherId, streamPartId) => {
                return this.doRequestGroupKey(groupKeyId, publisherId, streamPartId);
            }, config.encryption.maxKeyRequestsPerSecond);
        }
        else {
            this.ensureStarted = async () => {
                throw new StreamrClientError('Assertion failed', 'ASSERTION_FAILED');
            };
            this.requestGroupKey = async () => {
                throw new StreamrClientError('Assertion failed', 'ASSERTION_FAILED');
            };
        }
    }
    async doRequestGroupKey(groupKeyId, publisherId, streamPartId) {
        await this.ensureStarted();
        const requestId = uuidv4();
        const { message, request } = await this.createRequest(groupKeyId, streamPartId, publisherId, requestId);
        await this.networkNodeFacade.broadcast(message);
        this.pendingRequests.add(requestId);
        this.logger.debug('Sent group key request (waiting for response)', {
            groupKeyId,
            requestId,
            publisherId,
            keyEncryptionType: AsymmetricEncryptionType[request.encryptionType]
        });
    }
    async createRequest(groupKeyId, streamPartId, publisherId, requestId) {
        const request = {
            recipientId: toUserIdRaw(publisherId),
            requestId,
            publicKey: this.keyPair.getPublicKey(),
            groupKeyIds: [groupKeyId],
            encryptionType: this.keyPair.getEncryptionType(),
        };
        const erc1271contract = this.subscriber.getERC1271ContractAddress(streamPartId);
        const message = await this.messageSigner.createSignedMessage({
            messageId: new MessageID(StreamPartIDUtils.getStreamID(streamPartId), StreamPartIDUtils.getStreamPartition(streamPartId), Date.now(), 0, erc1271contract === undefined ? await this.identity.getUserId() : toUserId(erc1271contract), createRandomMsgChainId()),
            content: GroupKeyRequest.toBinary(request),
            contentType: ContentType.BINARY,
            messageType: StreamMessageType.GROUP_KEY_REQUEST,
            encryptionType: EncryptionType.NONE,
        }, erc1271contract === undefined ? this.identity.getSignatureType() : SignatureType.ERC_1271);
        return { message, request };
    }
    async onMessage(msg) {
        if (msg.messageType === StreamMessageType.GROUP_KEY_RESPONSE) {
            try {
                const { requestId, recipientId, groupKeys: encryptedGroupKeys, encryptionType } = GroupKeyResponse.fromBinary(msg.content);
                const recipientUserId = toUserId(recipientId);
                if (await this.isAssignedToMe(msg.getStreamPartID(), recipientUserId, requestId)) {
                    this.logger.debug('Handle group key response', { requestId });
                    this.pendingRequests.delete(requestId);
                    await validateStreamMessage(msg, this.streamRegistry, this.signatureValidator, this.config);
                    await Promise.all(encryptedGroupKeys.map(async (encryptedKey) => {
                        const key = await EncryptionUtil.decryptWithPrivateKey(encryptedKey.data, this.keyPair.getPrivateKey(), encryptionType);
                        await this.store.set(encryptedKey.id, msg.getPublisherId(), key);
                    }));
                }
            }
            catch (err) {
                this.logger.debug('Failed to handle group key response', { err });
            }
        }
    }
    async isAssignedToMe(streamPartId, recipientId, requestId) {
        if (this.pendingRequests.has(requestId)) {
            const myId = await this.identity.getUserId();
            const erc1271Contract = this.subscriber.getERC1271ContractAddress(streamPartId);
            return (recipientId === myId) || ((erc1271Contract !== undefined) && (recipientId === toUserId(erc1271Contract)));
        }
        return false;
    }
};
SubscriberKeyExchange = __decorate([
    scoped(Lifecycle.ContainerScoped),
    __param(1, inject(delay(() => StreamRegistry))),
    __param(5, inject(Tokens.Subscriber)),
    __param(6, inject(ConfigInjectionToken)),
    __param(7, inject(IdentityInjectionToken)),
    __metadata("design:paramtypes", [NetworkNodeFacade,
        StreamRegistry,
        SignatureValidator,
        MessageSigner,
        LocalGroupKeyStore, Function, Object, Identity,
        LoggerFactory])
], SubscriberKeyExchange);
export { SubscriberKeyExchange };
